/**
 * CreditGrantForm.tsx — Modal form for admin credit grants
 *
 * Purpose:
 *   Modal form for granting credits to a customer. Submits to
 *   POST /admin/credits/grant with creditType, amount, and optional expiry.
 *   Shows inline success notification and invokes onSuccess callback.
 *
 * Public API:
 *   - CreditGrantForm   Modal form component
 *
 * Architecture Context:
 *   Used by CustomerDetailPage. Zod validation before the API call.
 *
 * @module packages/billing-admin/src/components/customer/CreditGrantForm
 * @since 2026-06-01 (AT-50.02 — Plan & Customer Management Screens)
 */
import React, { useState } from 'react';
import { z } from 'zod';
import { useAdminAuth } from '../../contexts/AdminAuthContext';

const creditSchema = z.object({
  creditType: z.string().min(1, 'Credit type is required'),
  amount: z.number().min(0.01, 'Amount must be > 0'),
  expiryDate: z.string().optional(),
});

type CreditFormValues = z.infer<typeof creditSchema>;

/** Available credit type options. */
const CREDIT_TYPES = [
  { value: 'promotional', label: 'Promotional' },
  { value: 'refund', label: 'Refund' },
  { value: 'goodwill', label: 'Goodwill' },
  { value: 'correction', label: 'Correction' },
] as const;

interface CreditGrantFormProps {
  /** Customer to grant credit to. */
  customerId: string;
  /** Called after successful grant to trigger credit balance refresh. */
  onSuccess: () => void;
  /** Called when the user cancels. */
  onCancel: () => void;
}

/**
 * CreditGrantForm — modal form for granting credit to a customer.
 *
 * @param props - CreditGrantFormProps
 * @since 2026-06-01 (AT-50.02)
 */
export default function CreditGrantForm({ customerId, onSuccess, onCancel }: CreditGrantFormProps): React.JSX.Element {
  const { token, apiBaseUrl } = useAdminAuth();

  const [values, setValues] = useState<CreditFormValues>({ creditType: '', amount: 0, expiryDate: '' });
  const [errors, setErrors] = useState<Partial<Record<keyof CreditFormValues, string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showToast, setShowToast] = useState(false);

  /**
   * handleFieldChange — updates a form field and clears its error.
   *
   * @param field - Field name
   * @param value - New value
   * @since 2026-06-01 (AT-50.02)
   */
  function handleFieldChange(field: keyof CreditFormValues, value: string | number): void {
    setValues((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  }

  /**
   * handleSubmit — validates and submits POST /admin/credits/grant.
   *
   * @since 2026-06-01 (AT-50.02)
   */
  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setSubmitError(null);

    const parsed = creditSchema.safeParse({ ...values, expiryDate: values.expiryDate || undefined });
    if (!parsed.success) {
      const fieldErrors: Partial<Record<keyof CreditFormValues, string>> = {};
      parsed.error.issues.forEach((issue) => {
        const field = issue.path[0] as keyof CreditFormValues;
        fieldErrors[field] = issue.message;
      });
      setErrors(fieldErrors);
      return;
    }

    if (!token) { setSubmitError('Not authenticated'); return; }

    setSubmitting(true);
    try {
      const res = await fetch(`${apiBaseUrl}/admin/credits/grant`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_id: customerId,
          credit_type: parsed.data.creditType,
          amount: parsed.data.amount,
          expiry_date: parsed.data.expiryDate ?? null,
        }),
      });
      if (!res.ok) throw new Error(`Credit grant failed: ${res.status}`);
      setShowToast(true);
      setTimeout(() => { setShowToast(false); onSuccess(); }, 1500);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Credit grant failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div>
      <h2 className="mb-4 text-xl font-semibold text-gray-900">Grant Credit</h2>
      {showToast && <div className="mb-4 rounded-md bg-green-50 p-3 text-sm text-green-700">Credit granted successfully!</div>}
      {submitError && <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-700">{submitError}</div>}
      <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
        <div>
          <label htmlFor="creditType" className="block text-sm font-medium text-gray-700">Credit Type</label>
          <select
            id="creditType"
            value={values.creditType}
            onChange={(e) => handleFieldChange('creditType', e.target.value)}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          >
            <option value="">Select type…</option>
            {CREDIT_TYPES.map((ct) => <option key={ct.value} value={ct.value}>{ct.label}</option>)}
          </select>
          {errors.creditType && <p className="mt-1 text-xs text-red-600">{errors.creditType}</p>}
        </div>
        <div>
          <label htmlFor="amount" className="block text-sm font-medium text-gray-700">Amount</label>
          <input
            id="amount" type="number" min="0.01" step="0.01"
            value={values.amount}
            onChange={(e) => handleFieldChange('amount', parseFloat(e.target.value) || 0)}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
          {errors.amount && <p className="mt-1 text-xs text-red-600">{errors.amount}</p>}
        </div>
        <div>
          <label htmlFor="expiryDate" className="block text-sm font-medium text-gray-700">
            Expiry Date <span className="font-normal text-gray-400">(optional)</span>
          </label>
          <input
            id="expiryDate" type="date"
            value={values.expiryDate ?? ''}
            onChange={(e) => handleFieldChange('expiryDate', e.target.value)}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onCancel} disabled={submitting}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            Cancel
          </button>
          <button type="submit" disabled={submitting}
            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
            {submitting ? 'Granting…' : 'Grant Credit'}
          </button>
        </div>
      </form>
    </div>
  );
}
